import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { bracketShape } from "@alpha/engine";
import { api, apiErrorMessage } from "../../../lib/api";
import { trackBracketCreated, trackSeedingFinalized } from "../../../lib/analytics";
import { useAdminMutation } from "../../../lib/adminData";
import { bracketDeleteConfirm } from "../../../lib/confirmCopy";
import { moveItem } from "../../../lib/reorder";
import {
  ADVANCE_ALL,
  advanceOptions,
  bracketRounds,
  bracketShapeSummary,
  parseAdvance,
} from "../../../lib/bracket";
import { divisionBracket, divisionSeeds, teamAttrsById, teamName } from "../../../lib/bundle";
import type { Caps } from "../../../lib/tournamentPhases";
import type { Bundle, Division } from "../../../lib/types";
import { ScoreCard } from "../../../components/ScoreCard";
import { Badge, Button, cx, SectionCard, Select } from "../../../components/ui";
import { orderedDivisions } from "./Divisions";
import { phasePath } from "./PhaseRail";
import { ButtonLink } from "../../../components/ui";
import type { TournamentContext } from "./TournamentShell";

/**
 * Seeding and the bracket (console spec §5.6): the seeds in an order the
 * director can adjust before locking, the shape, and once it exists the
 * bracket's games as score cards — the same card Scores uses, so a final is
 * entered from either page.
 */
export function Playoffs() {
  const { tid, bundle, caps, now } = useOutletContext<TournamentContext>();
  return <SeedingBracket tid={tid} bundle={bundle} caps={caps} now={now} />;
}

/**
 * Seeding and the bracket it feeds, division by division (spec §7.9, §7.10).
 *
 * Setup sits here rather than under Divisions & pools because the choice it
 * makes — how many teams advance — is only legible next to the seed list it
 * cuts and the buttons that act on it. A director looking at "Top 8" wants to
 * see which eight.
 */
function SeedingBracket({
  tid,
  bundle,
  caps,
  now,
}: {
  tid: string;
  bundle: Bundle;
  caps: Caps;
  now: Date;
}) {
  return (
    <SectionCard
      title="Seeding & bracket"
      description="Choose how many teams reach each bracket, finalize seeds once pool play wraps up, then create it."
    >
      {bundle.divisions.length === 0 ? (
        <p className="text-sm text-slate-400">No divisions yet — add one under Setup.</p>
      ) : null}
      {orderedDivisions(bundle.divisions).map((d) => (
        <DivisionBracket key={d.id} tid={tid} bundle={bundle} division={d} caps={caps} now={now} />
      ))}
    </SectionCard>
  );
}

/**
 * One division's bracket: the shape it will take, the seeds it is built from,
 * and the three actions that produce it.
 *
 * Its own component because each division needs state a loop body cannot hold
 * — the rebuild confirm and the error from whichever of these calls failed.
 * Sharing one error between divisions would report a 9U failure on the 12U
 * card.
 */
function DivisionBracket({
  tid,
  bundle,
  division,
  caps,
  now,
}: {
  tid: string;
  bundle: Bundle;
  division: Division;
  caps: Caps;
  now: Date;
}) {
  const [error, setError] = useState<string>();
  const [confirmingRebuild, setConfirmingRebuild] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingRefinalize, setConfirmingRefinalize] = useState(false);
  const refresh = useAdminMutation(tid, () => Promise.resolve());

  const seeds = divisionSeeds(bundle, division.id);
  const locked = Boolean(division.lockedSeeds);
  const bracketGames = bundle.games.filter((g) => g.divisionId === division.id && g.type === "bracket");
  const hasBracket = bracketGames.length > 0;
  const bracketPlayed = bracketGames.some((g) => g.status === "final" || g.status === "forfeit");
  const poolGames = bundle.games.filter((g) => g.divisionId === division.id && g.type === "pool");
  const poolFinished = poolGames.filter((g) => g.status === "final" || g.status === "forfeit").length;
  const poolSettled = poolGames.every((g) => g.status !== "scheduled");
  const poolPlayDone = poolGames.length > 0 && poolSettled && poolFinished > 0;
  const pools = bundle.pools.filter((p) => p.divisionId === division.id);
  const unpooled = pools.length > 0 ? bundle.entries.filter((e) => e.divisionId === division.id && e.poolId === null) : [];

  // The order the director is about to lock: the computed standings order (or
  // the locked one, once locked), adjusted by hand. Keyed on that source so a
  // refetch that changes it resets the adjustments rather than keeping a stale
  // list — a reorder is a decision about the order on screen.
  const computed = seeds.seeds.map((x) => x.teamId);
  const source = division.lockedSeeds ?? computed;
  const sourceKey = source.join(",");
  const [draft, setDraft] = useState<{ key: string; order: string[] }>({ key: sourceKey, order: source });
  const order = draft.key === sourceKey ? draft.order : source;
  const setOrder = (next: string[]) => setDraft({ key: sourceKey, order: next });
  const standingsChanged = locked && computed.join(",") !== division.lockedSeeds!.join(",");
  const byTeam = new Map(seeds.seeds.map((x) => [x.teamId, x]));

  // What Create bracket will actually build from: the locked list once there is
  // one — which is the list the server generates against — and otherwise the
  // seeds as they stand today. Using the entry count instead would describe a
  // different bracket than the one the button makes.
  const seedCount = division.lockedSeeds?.length ?? seeds.seeds.length;
  const advance = division.bracket?.advance ?? "all";
  const shape = bracketShape(seedCount, advance);

  const run = (p: Promise<unknown>, fallback: string) =>
    p
      .then(() => {
        setError(undefined);
        refresh.mutate();
      })
      .catch((e) => setError(apiErrorMessage(e, fallback)));

  const chooseAdvance = (value: string) =>
    void run(
      api.put(`/admin/tournaments/${tid}/divisions/${division.id}`, {
        bracket: { format: "singleElim", advance: parseAdvance(value) },
      }),
      "Could not save the bracket setup.",
    );

  const finalize = () => {
    // Under an unplayed bracket, re-finalizing is a two-step: the new seeds
    // only reach the bracket once it is rebuilt, and the second tap says so.
    if (locked && hasBracket && !confirmingRefinalize) {
      setConfirmingRefinalize(true);
      return;
    }
    setConfirmingRefinalize(false);
    void run(
      api
        .post(`/admin/tournaments/${tid}/divisions/${division.id}/finalize-seeding`, { order })
        .then(trackSeedingFinalized),
      "Could not finalize seeding.",
    );
  };

  /**
   * Create the bracket, or rebuild an existing one in place. Rebuilding is one
   * request rather than Remove-then-Create so the API can carry each game's
   * time and field onto the game that takes its slot — but it does clear the
   * scores, so it asks first, the same two-step confirm the status buttons use.
   */
  const build = () => {
    if (hasBracket && !confirmingRebuild) {
      setConfirmingRebuild(true);
      return;
    }
    setConfirmingRebuild(false);
    void run(
      api
        .post(`/admin/tournaments/${tid}/divisions/${division.id}/bracket`, {
          replace: hasBracket,
        })
        .then(() => trackBracketCreated(hasBracket)),
      hasBracket ? "Could not rebuild the bracket." : "Could not create the bracket.",
    );
  };

  const remove = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setConfirmingDelete(false);
    void run(
      api.del(`/admin/tournaments/${tid}/divisions/${division.id}/bracket`),
      "Could not delete the bracket.",
    );
  };

  const finalizeBlocked =
    pools.length === 0
      ? "Add pools under Setup"
      : unpooled.length > 0
        ? `${unpooled.length} ${unpooled.length === 1 ? "team has" : "teams have"} no pool — assign them under Teams`
        : locked && bracketPlayed
          ? "Delete the bracket to re-seed"
          : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-wrap items-center gap-2 font-display font-semibold text-navy-950">
          {division.name}
          {locked ? (
            <Badge tone="green">Seeds locked</Badge>
          ) : (
            <Badge tone="amber">Provisional</Badge>
          )}
          {standingsChanged ? <Badge tone="amber">Standings changed since seeds were locked</Badge> : null}
          {/* Whether seeding is premature, at a glance. */}
          <span className="font-sans text-xs font-normal tabular-nums text-slate-500">
            Pool play {poolFinished} / {poolGames.length} final
          </span>
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {finalizeBlocked ? (
            <span className="text-xs text-slate-500">{finalizeBlocked}</span>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={finalize}
              onBlur={() => setConfirmingRefinalize(false)}
            >
              {confirmingRefinalize
                ? "Confirm — then rebuild the bracket"
                : locked
                  ? "Re-finalize"
                  : "Finalize seeding"}
            </Button>
          )}
          <Button
            size="sm"
            variant={confirmingRebuild ? "danger" : "primary"}
            disabled={!locked}
            onClick={build}
            onBlur={() => setConfirmingRebuild(false)}
          >
            {hasBracket
              ? confirmingRebuild
                ? "Confirm rebuild"
                : "Rebuild bracket"
              : "Create bracket"}
          </Button>
          {hasBracket ? (
            <Button
              variant={confirmingDelete ? "danger" : "secondary"}
              size="sm"
              onClick={remove}
              onBlur={() => setConfirmingDelete(false)}
            >
              {confirmingDelete
                ? bracketDeleteConfirm(
                    bracketGames.length,
                    bracketGames.filter((g) => g.status === "final" || g.status === "forfeit").length,
                  )
                : "Delete bracket"}
            </Button>
          ) : null}
        </div>
      </div>

      {/*
        The setup line. The format is stated rather than offered: single
        elimination is the only one the engine builds (spec §7.10), and a
        one-option dropdown reads as a choice that is coming.
      */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-slate-100 pt-3">
        <Badge tone="slate">Single elimination</Badge>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          Teams advancing
          <Select
            uiSize="sm"
            className="w-40"
            aria-label={`Teams advancing to the ${division.name} bracket`}
            value={typeof advance === "number" ? String(advance) : ADVANCE_ALL}
            onChange={(e) => chooseAdvance(e.target.value)}
          >
            {advanceOptions(seedCount, advance).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>
        <span className="text-xs text-slate-500">{bracketShapeSummary(shape)}</span>
      </div>

      {hasBracket ? (
        <p className="mt-2 text-xs text-slate-500">
          {confirmingRebuild
            ? "Rebuilding keeps each game’s time and field, and clears the scores already entered."
            : "The bracket already exists — rebuild it to apply a change to this setup or to the seeds."}
        </p>
      ) : null}
      {!locked ? (
        <p className="mt-2 text-xs text-slate-500">
          Finalize seeding to lock this order in; the bracket is built from it.
          {!poolPlayDone && poolGames.length > 0 ? " Finalize once pool play is done." : ""}
        </p>
      ) : null}
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}

      <ol className="mt-3 space-y-1.5">
        {order.length === 0 ? (
          <li className="text-xs text-slate-400">No entries seeded yet.</li>
        ) : (
          order.map((teamId, i) => {
            const s = byTeam.get(teamId);
            const canMove = !finalizeBlocked && !(locked && bracketPlayed);
            return (
              <li
                key={teamId}
                className={cx(
                  "flex items-center gap-2.5 text-sm",
                  // Past the cut: still listed, because a director re-reads the
                  // order they are about to lock, but plainly not in the bracket.
                  shape.games > 0 && i >= shape.advancing ? "text-slate-400" : "text-slate-700",
                )}
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-bold tabular-nums text-slate-600 ring-1 ring-slate-200">
                  {i + 1}
                </span>
                {teamName(bundle, teamId)}
                {teamAttrsById(bundle, teamId) ? (
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    {teamAttrsById(bundle, teamId)}
                  </span>
                ) : null}
                {s?.unresolved ? <Badge tone="amber">unresolved</Badge> : null}
                {shape.games > 0 && i < shape.byes ? <Badge tone="slate">bye</Badge> : null}
                {canMove ? (
                  <span className="ml-auto flex items-center gap-0.5">
                    <Button size="sm" variant="ghost" aria-label={`Move ${teamName(bundle, teamId)} up`} disabled={i === 0} onClick={() => setOrder(moveItem(order, i, -1))}>
                      ↑
                    </Button>
                    <Button size="sm" variant="ghost" aria-label={`Move ${teamName(bundle, teamId)} down`} disabled={i === order.length - 1} onClick={() => setOrder(moveItem(order, i, 1))}>
                      ↓
                    </Button>
                  </span>
                ) : null}
              </li>
            );
          })
        )}
      </ol>
      {unpooled.length > 0 ? (
        <p className="mt-2 text-xs text-slate-500">
          No pool:{" "}
          {unpooled.map((e) => teamName(bundle, e.teamId)).join(", ")}
          {" — "}
          <ButtonLink variant="ghost" size="sm" to={phasePath(tid, "teams")} className="px-1 py-0 text-xs">
            assign them under Teams
          </ButtonLink>
        </p>
      ) : null}

      {hasBracket ? (
        <div className="mt-4 space-y-4 border-t border-slate-100 pt-4">
          {bracketRounds(divisionBracket(bundle, division.id).games).map((round) => (
            <div key={round.round}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                {round.name}
              </h3>
              <div className="grid gap-3 lg:grid-cols-2">
                {round.games.map((g) => {
                  const raw = bundle.games.find((x) => x.id === g.gameId);
                  return raw ? (
                    <ScoreCard
                      key={g.gameId}
                      tid={tid}
                      bundle={bundle}
                      game={raw}
                      resolved={g}
                      caps={caps}
                      now={now}
                    />
                  ) : null;
                })}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
